import { z } from 'zod';

// src/envelope.ts
var zRecorded = z.object({
  attendance: z.number().min(0).max(1),
  rsvp: z.number().min(0).max(1),
  demographics: z.number().min(0).max(1),
  questions: z.number().min(0).max(1)
});

// src/envelope.ts
var zAnomalySeverity = z.enum(["info", "warn", "critical"]);
var zAnomaly = z.object({
  key: z.string(),
  severity: zAnomalySeverity,
  scope: z.string(),
  metric: z.string(),
  observed: z.union([z.number(), z.string()]),
  threshold: z.union([z.number(), z.string()]),
  message: z.string()
});
var zEnvelopeMeta = z.object({
  mock: z.boolean().optional(),
  anomalies: z.array(zAnomaly).default([])
});
var Envelope = (data) => z.object({
  generated_at: z.string(),
  source_pulled_at: z.string(),
  scope: z.string(),
  recorded: zRecorded,
  meta: zEnvelopeMeta,
  data
});
var zDistrictTrend = z.enum(["accelerating", "stable", "stalling"]);
var zDistrict = z.object({
  id: z.string(),
  he: z.string(),
  col: z.number().int(),
  row: z.number().int(),
  reached: z.number().int().nonnegative(),
  rsvp_yes: z.number().int().nonnegative(),
  attended: z.number().int().nonnegative(),
  walk_in: z.number().int().nonnegative(),
  events: z.number().int().nonnegative(),
  recorded_pct: z.number().min(0).max(1),
  trend: zDistrictTrend
});
var zDistricts = z.array(zDistrict);
var zTotals = z.object({
  events: z.number().int().nonnegative(),
  reached: z.number().int().nonnegative(),
  rsvp_yes: z.number().int().nonnegative(),
  attended: z.number().int().nonnegative(),
  walk_in: z.number().int().nonnegative(),
  records: z.number().int().nonnegative()
});
var zCity = z.object({
  he: z.string(),
  district: z.string(),
  events: z.number().int().nonnegative(),
  reached: z.number().int().nonnegative(),
  rsvp_yes: z.number().int().nonnegative(),
  attended: z.number().int().nonnegative(),
  walk_in: z.number().int().nonnegative()
});
var zCities = z.array(zCity);
var zOrganizerFormatMix = z.enum([
  "rally-heavy",
  "mixed",
  "parlor-heavy"
]);
var zOrganizer = z.object({
  he: z.string(),
  district: z.string(),
  events: z.number().int().nonnegative(),
  rsvp_yes: z.number().int().nonnegative(),
  attended: z.number().int().nonnegative(),
  walk_in: z.number().int().nonnegative(),
  format_mix: zOrganizerFormatMix,
  insufficient_data: z.boolean().optional()
});
var zOrganizers = z.array(zOrganizer);
var zFormat = z.object({
  id: z.string(),
  he: z.string(),
  events: z.number().int().nonnegative(),
  reached: z.number().int().nonnegative(),
  rsvp_yes: z.number().int().nonnegative(),
  attended: z.number().int().nonnegative(),
  walk_in: z.number().int().nonnegative(),
  avg_per_event: z.number().nonnegative()
});
var zFormats = z.array(zFormat);
var zChannel = z.object({
  id: z.string(),
  he: z.string(),
  rate_overall: z.number().min(0).max(1),
  total_signups: z.number().int().nonnegative(),
  anomaly: z.boolean().optional(),
  note: z.string().optional()
});
var zChannels = z.array(zChannel);
var zDistrictRateMap = z.record(z.string(), z.number().min(0).max(1));
var zChannelByDistrict = z.record(z.string(), zDistrictRateMap);
var zTrendSeries = z.array(z.number().int().nonnegative());
var zTrends = z.record(z.string(), zTrendSeries);
var zWeekLabels = z.array(z.string());
var zCampaignTrend = z.array(z.number().int().nonnegative());
var zSpeakerCell = z.object({
  events: z.number().int().nonnegative(),
  attended_avg: z.number().nonnegative().nullable(),
  yes_show: z.number().min(0).max(1).nullable(),
  n: z.number().int().nonnegative()
});
var zSpeakerFormatRow = z.record(z.string(), zSpeakerCell);
var zSpeakerByFormat = z.record(z.string(), zSpeakerFormatRow);
var zTopSpeaker = z.object({
  /** Airtable record id of the MK row, or a synthetic id (`nonmk:...`)
   *  for curated non-MK speakers (former generals, party officials, etc.). */
  mk_id: z.string(),
  /** Display name (`first + ' ' + last` formula on the MK table). */
  name: z.string(),
  /** Affiliation chip. Party name for MKs (`בנט 2026` | `יש עתיד`), or
   *  a role/title for non-MK speakers (e.g. `אלוף במיל'`, `מנכ״לית בנט 2026`). */
  party: z.string().nullable(),
  /** Number of distinct events this MK appeared at. */
  events: z.number().int().nonnegative(),
  /** Sum of attended participants across those events. */
  attended: z.number().int().nonnegative(),
  /** RSVP-yes → attendance conversion across those events. Null when
   *  the denominator (rsvp_yes total) is zero. */
  yes_show: z.number().min(0).max(1).nullable(),
  /** Dominant event format, e.g. `'town-hall'`. Null when none of this
   *  speaker's events had a format set. */
  format_mix: z.string().nullable()
});
var zTopSpeakers = z.array(zTopSpeaker);
var zSpeakers = z.object({
  matrix: zSpeakerByFormat,
  top_speakers: zTopSpeakers
});
var zCoverageWeight = z.object({
  id: z.string(),
  coverage: z.number().nonnegative(),
  weight: z.number().nonnegative(),
  eligible_k: z.number().nonnegative()
});
var zCoverageWeights = z.array(zCoverageWeight);
var zReturning = z.object({
  id: z.string(),
  he: z.string(),
  total: z.number().int().nonnegative(),
  once: z.number().int().nonnegative(),
  twice: z.number().int().nonnegative(),
  three_four: z.number().int().nonnegative(),
  five_plus: z.number().int().nonnegative(),
  committed_share: z.number().min(0).max(1)
});
var zReturnings = z.array(zReturning);
var zGotv = z.object({
  id: z.string(),
  he: z.string(),
  eligible: z.number().int().nonnegative(),
  recent_4w: z.number().int().nonnegative(),
  recent_4_12w: z.number().int().nonnegative(),
  older_12w: z.number().int().nonnegative(),
  phone: z.number().int().nonnegative(),
  email: z.number().int().nonnegative(),
  last_contact_days: z.number().int().nonnegative()
});
var zGotvs = z.array(zGotv);
var zThemeDistrictCell = z.object({
  _total: z.number().int().nonnegative()
}).catchall(z.number().int().nonnegative());
var zThemeByDistrict = z.record(z.string(), zThemeDistrictCell);
var zNoShowFeature = z.object({
  id: z.string(),
  he: z.string(),
  importance: z.number().min(0).max(1),
  ci: z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)]),
  desc: z.string()
});
var zNoShowFeatures = z.array(zNoShowFeature);
var zLeadtimeBucket = z.object({
  he: z.string(),
  count: z.number().int().nonnegative(),
  attended: z.number().int().nonnegative(),
  rate: z.number().min(0).max(1)
});
var zLeadtimeBuckets = z.array(zLeadtimeBucket);
var zFillTime = z.object({
  id: z.string(),
  he: z.string(),
  median_hours: z.number().nonnegative(),
  p10_hours: z.number().nonnegative(),
  p90_hours: z.number().nonnegative(),
  events_filled: z.number().int().nonnegative(),
  events_never_filled: z.number().int().nonnegative(),
  interpretation: z.string()
});
var zFillTimes = z.array(zFillTime);
var zModuleHealth = z.enum(["live", "gap", "anomaly"]);
var zModulePhase = z.enum(["P2", "P3"]);
var zModuleSpec = z.object({
  id: z.string(),
  he: z.string(),
  sub: z.string(),
  owner: z.string(),
  updated: z.string(),
  health: zModuleHealth,
  phase: zModulePhase.optional(),
  pinned: z.boolean().optional()
});
var zSuite = z.object({
  id: z.string(),
  he: z.string(),
  en: z.string(),
  icon: z.string(),
  abstract: z.string(),
  color: z.string(),
  href: z.string().optional(),
  modules: z.array(zModuleSpec)
});
var zSuites = z.array(zSuite);
var zBriefSignalTone = z.enum(["up", "flat", "down"]);
var zBriefSignal = z.object({
  suite: z.string(),
  he: z.string(),
  v: z.string(),
  delta: z.string(),
  tone: zBriefSignalTone
});
var zBrief = z.object({
  date: z.string(),
  edition: z.string(),
  headline: z.string(),
  body: z.string(),
  signals: z.array(zBriefSignal)
});
var zPinned = z.object({
  suite: z.string(),
  module: z.string(),
  he: z.string(),
  metric: z.string(),
  delta: z.string()
});
var zPinneds = z.array(zPinned);
var zWorkspace = z.object({
  id: z.string(),
  he: z.string(),
  sub: z.string(),
  modules: z.number().int().nonnegative(),
  owner: z.string(),
  updated: z.string()
});
var zWorkspaces = z.array(zWorkspace);
var zAlertLevel = z.enum(["critical", "warn", "info", "alert"]);
var zAlert = z.object({
  id: z.string(),
  level: zAlertLevel,
  he: z.string(),
  suite: z.string(),
  module: z.string(),
  time: z.string()
});
var zAlerts = z.array(zAlert);
var zRecent = z.object({
  suite: z.string(),
  module: z.string(),
  he: z.string(),
  time: z.string()
});
var zRecents = z.array(zRecent);
var zUserStatus = z.enum(["active", "pending", "inactive"]);
var zUser = z.object({
  id: z.string(),
  name: z.string(),
  initials: z.string(),
  role: z.string(),
  team: z.string(),
  region: z.string().optional(),
  regions: z.array(z.string()).optional(),
  email: z.string(),
  phone: z.string(),
  last: z.string(),
  joined: z.string(),
  status: zUserStatus,
  mfa: z.boolean(),
  you: z.boolean().optional()
});
var zUsers = z.array(zUser);
var zRoleScope = z.enum([
  "all",
  "view-all",
  "selected",
  "regional-read"
]);
var zRole = z.object({
  id: z.string(),
  he: z.string(),
  short: z.string(),
  scope: zRoleScope,
  canAdmin: z.boolean().optional(),
  desc: z.string()
});
var zRoles = z.array(zRole);
var zPermLevel = z.enum(["admin", "edit", "view"]);
var zPermRow = z.record(z.string(), zPermLevel.nullable());
var zPerms = z.record(z.string(), zPermRow);
var zAudit = z.object({
  id: z.string(),
  who: z.string(),
  what: z.string(),
  target: z.string(),
  time: z.string(),
  sys: z.string()
});
var zAudits = z.array(zAudit);

export { Envelope, zAlert, zAlertLevel, zAlerts, zAnomaly, zAnomalySeverity, zAudit, zAudits, zBrief, zBriefSignal, zBriefSignalTone, zCampaignTrend, zChannel, zChannelByDistrict, zChannels, zCities, zCity, zCoverageWeight, zCoverageWeights, zDistrict, zDistrictRateMap, zDistrictTrend, zDistricts, zEnvelopeMeta, zFillTime, zFillTimes, zFormat, zFormats, zGotv, zGotvs, zLeadtimeBucket, zLeadtimeBuckets, zModuleHealth, zModulePhase, zModuleSpec, zNoShowFeature, zNoShowFeatures, zOrganizer, zOrganizerFormatMix, zOrganizers, zPermLevel, zPermRow, zPerms, zPinned, zPinneds, zRecent, zRecents, zRecorded, zReturning, zReturnings, zRole, zRoleScope, zRoles, zSpeakerByFormat, zSpeakerCell, zSpeakerFormatRow, zSpeakers, zSuite, zSuites, zThemeByDistrict, zThemeDistrictCell, zTopSpeaker, zTopSpeakers, zTotals, zTrendSeries, zTrends, zUser, zUserStatus, zUsers, zWeekLabels, zWorkspace, zWorkspaces };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map